import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronDown } from "react-icons/fi";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import withAuth from "../../utils/withAuth";
import ReportNavBar from "../../components/ReportNavBar";
import { numberFormatWithGst } from "../../components/numberFormatWithGst";
import useSkillCenterMap from "../../hooks/useSkillCenterMap";
import "./TodayLiveCallCenterReports.scss";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const formatOrderAmount = (value) => numberFormatWithGst(Number(value || 0), "INR", 0);

const TodayLiveCallCenterReports = () => {
    const [process, setProcess] = useState([]);  
    const [summary, setSummary] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Date range filters
    const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [appliedDate, setAppliedDate] = useState({ from: "", to: "" });

    // Skill search (applied)
    const [skillQuery, setSkillQuery] = useState("");
    const [appliedSkillQuery, setAppliedSkillQuery] = useState("");

    // Center multi-select (checkbox)
    const [centerOpen, setCenterOpen] = useState(false);
    const [centerSearch, setCenterSearch] = useState("");
    const [selectedCenters, setSelectedCenters] = useState(new Set()); // working
    const [appliedCenters, setAppliedCenters] = useState(new Set());  // applied

    const [showGroups, setShowGroups] = useState(true);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");
    const { deriveCenterName } = useSkillCenterMap();

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/today-live-call-center-reports");

        // default load for today
        setAppliedDate({ from: fromDate, to: toDate });
        getLiveCallWithProcessName(ppcid, fromDate, toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (d1) qs.set("start_date", d1);
            if (d2) qs.set("end_date", d2);

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-center-live-call-with-order-amount-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const p = res?.data?.process || {};
                const rows = Array.isArray(p?.rows) ? p.rows : [];
                setProcess(rows);
                setSummary(p?.summary || null);

                // initialize center selections to ALL (only on first load / empty selection)
                // We'll build centers later from grouped data, but we can keep empty now.
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };
    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const showProcessMobile = Number(ppcid) === 31;
    const getSkillLabel = (item) => {
        const mobile = String(item?.processMobile || "").trim();
        return showProcessMobile && mobile ? mobile : String(item?.code || "");
    };
    
    // Group raw data
    const groupedDataRaw = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const processMobile = String(row?.process_mobile || "").trim();
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const orderAmount = Number(row?.order_amount || 0);
            const spend = Number(row?.spend_today || 0);
            const groupName = deriveCenterName(code);

            if (!groups[groupName]) {
                groups[groupName] = { name: groupName, totalCalls: 0, totalOrders: 0, totalAmount: 0, totalSpend: 0, items: [] };
            }

            groups[groupName].totalCalls += calls;
            groups[groupName].totalOrders += orders;
            groups[groupName].totalAmount += orderAmount;
            groups[groupName].totalSpend += spend;

            groups[groupName].items.push({
                code,
                processMobile,
                calls,
                orders,
                orderAmount,
                spend,
                perCall: perCallCost(spend, calls),
                perf: calls ? (orders / calls) * 100 : 0,
            });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.perCall = perCallCost(g.totalSpend, g.totalCalls);
            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process, deriveCenterName]);

    // center options (for checkbox list)
    const centerOptions = useMemo(() => {
        return Array.from(new Set(groupedDataRaw.map((g) => g.name).filter(Boolean))).sort((a, b) =>
            a.localeCompare(b)
        );
    }, [groupedDataRaw]);

    const filteredCenterOptions = useMemo(() => {
        const q = String(centerSearch || "").trim().toLowerCase();
        if (!q) return centerOptions;
        return centerOptions.filter((c) => c.toLowerCase().includes(q));
    }, [centerOptions, centerSearch]);

    const toggleCenter = (name) => {
        setSelectedCenters((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onApplyCenters = () => {
        setAppliedCenters(new Set(selectedCenters));
        setCenterOpen(false);
    };

    const centerButtonText = useMemo(() => {
        if (!appliedCenters || appliedCenters.size === 0) return "All Centers";
        if (appliedCenters.size === centerOptions.length) return "All Centers";
        if (appliedCenters.size === 1) return Array.from(appliedCenters)[0];
        return `${appliedCenters.size} Centers`;
    }, [appliedCenters, centerOptions]);

    // apply Center + Skill filter on grouped data
    const groupedData = useMemo(() => {
        const q = String(appliedSkillQuery || "").trim().toLowerCase();
        const centers = appliedCenters && appliedCenters.size ? appliedCenters : new Set(centerOptions);

        let list = groupedDataRaw.filter((g) => centers.has(g.name));

        if (q) {
            list = list
                .map((g) => {
                    const items = (g.items || []).filter((it) =>
                        String(it.code || "").toLowerCase().includes(q) ||
                        String(it.processMobile || "").toLowerCase().includes(q)
                    );
                    if (!items.length) return null;

                    const totalCalls = items.reduce((s, it) => s + (Number(it.calls) || 0), 0);
                    const totalOrders = items.reduce((s, it) => s + (Number(it.orders) || 0), 0);
                    const totalAmount = items.reduce((s, it) => s + (Number(it.orderAmount) || 0), 0);
                    const totalSpend = items.reduce((s, it) => s + (Number(it.spend) || 0), 0);

                    return {
                        ...g,
                        items,
                        totalCalls,
                        totalOrders,
                        totalAmount,
                        totalSpend,
                        perf: totalCalls ? (totalOrders / totalCalls) * 100 : 0,
                        perCall: perCallCost(totalSpend, totalCalls),
                    };
                })
                .filter(Boolean);
        }

        return list;
    }, [groupedDataRaw, appliedCenters, centerOptions, appliedSkillQuery]);

    // Prefer backend summary
    const liveCallCount = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const liveOrderCount = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const liveSpendToday = useMemo(() => {
        if (summary && typeof summary.total_spend_today !== "undefined") return Number(summary.total_spend_today) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.spend_today) || 0), 0);
    }, [process, summary]);

    const liveOrderAmount = useMemo(() => {
        if (summary && typeof summary.total_order_amount !== "undefined") return Number(summary.total_order_amount) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_amount) || 0), 0);
    }, [process, summary]);

    const livePerf = useMemo(
        () => (liveCallCount ? (liveOrderCount / liveCallCount) * 100 : 0),
        [liveCallCount, liveOrderCount]
    );

    const livePerCall = useMemo(() => perCallCost(liveSpendToday, liveCallCount), [liveSpendToday, liveCallCount]);

    // Apply button for date + filters
    const onApplyFilters = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });
        setAppliedSkillQuery(skillQuery || "");
        setAppliedCenters(new Set(selectedCenters));
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d1, d2);
    };

    const onResetFilters = () => {
        const today = new Date().toISOString().slice(0, 10);
        setFromDate(today);
        setToDate(today);
        setSkillQuery("");
        setAppliedSkillQuery("");

        setSelectedCenters(new Set());
        setAppliedCenters(new Set());

        setAppliedDate({ from: today, to: today });
        setCenterSearch("");
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, today, today);
    };

    const headerTitle = useMemo(() => {
        const d1 = appliedDate.from || fromDate;
        const d2 = appliedDate.to || toDate;
        if (d1 && d2) return `Live Report (${d1} to ${d2})`;
        return "Live Report";
    }, [appliedDate, fromDate, toDate]);

    return (
        <div className="misDashboard today-live-center-report-page">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                {/* Top Summary */}
                <div className="live-report-head">
                    <div>
                        <p className="live-report-kicker">Realtime PPC Call View</p>
                        <h5 className="livecallcount">{headerTitle}</h5>
                    </div>
                    <div className="live-summary-strip">
                        <span className="metric-card metric-calls"><i>C</i><b>{isLoading ? "..." : fmt(liveCallCount)}</b></span>
                        <span className="metric-card metric-orders"><i>O</i><b>{isLoading ? "..." : fmt(liveOrderCount)}</b></span>
                        <span className="metric-card metric-amount"><i>₹</i><b>{isLoading ? "..." : formatOrderAmount(liveOrderAmount)}</b></span>
                    </div>
                </div>

                {/* Filters */}
                <div className="card live-filter-card mt-2">
                    <div className="card-body p-2">
                        <div className="filterRow">
                            <div className="filterBox">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="filterBox">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Center checkbox dropdown */}
                            <div className="filterBox">
                                <label>Center Name</label>
                                <div className="centerDropWrap">
                                    <button
                                        type="button"
                                        className="centerDropBtn"
                                        onClick={() => setCenterOpen((s) => !s)}
                                        disabled={isLoading || !centerOptions.length}
                                    >
                                        <span style={{ fontWeight: 600 }}>{centerButtonText}</span>
                                        <FiChevronDown style={{ opacity: 0.7 }} />
                                    </button>

                                    {centerOpen && (
                                        <div className="centerDropMenu">
                                            <div className="centerDropHead">
                                                <input
                                                    className="centerSearch"
                                                    value={centerSearch}
                                                    onChange={(e) => setCenterSearch(e.target.value)}
                                                    placeholder="Search center..."
                                                />
                                            </div>

                                            <div className="centerList">
                                                {filteredCenterOptions.length ? (
                                                    filteredCenterOptions.map((name) => (
                                                        <label key={name} className="centerItem">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCenters.has(name)}
                                                                onChange={() => toggleCenter(name)}
                                                            />
                                                            <span>{name}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                        No centers found
                                                    </div>
                                                )}
                                            </div>

                                            <div className="centerFoot">
                                                <button type="button" className="btnresetall" onClick={onApplyCenters} style={{ width: "100%" }}>
                                                    Apply Centers
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Skill search */}
                            <div className="filterBox">
                                <label>Search Skill</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search skill..."
                                    value={skillQuery}
                                    onChange={(e) => setSkillQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <button className="btnskillapply" onClick={onApplyFilters} disabled={isLoading}>
                                Apply
                            </button>

                            <button className="btnresetall" onClick={onResetFilters} disabled={isLoading}>
                                Reset
                            </button>

                            <button className="btnskill" onClick={() => setShowGroups((s) => !s)} disabled={isLoading}>
                                {showGroups ? "Hide Center View" : "Show Center View"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Cards */}
                {showGroups && (
                    <div className="row mt-3 livecallbody">
                        {isLoading
                            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                            : groupedData.map((group, idx) => (
                                <div className="col-xl-3 col-lg-4 col-md-6 mb-3" key={idx}>
                                    <div className="live-center-card">
                                        <div className="live-center-card-head">
                                            <div className="sh-left">{group.name}</div>
                                            <div className="live-center-metrics">
                                                <span className="metric-card metric-calls"><i>C</i><b>{fmt(group.totalCalls)}</b></span>
                                                <span className="metric-card metric-orders"><i>O</i><b>{fmt(group.totalOrders)}</b></span>
                                                <span className="metric-card metric-amount"><i>₹</i><b>{formatOrderAmount(group.totalAmount)}</b></span>
                                            </div>
                                        </div>
                                        <div className="live-table-scrolls">
                                            <table className="table mb-0 mini-table live-skill-table table-bordred">
                                                    <thead className="stickyHead">
                                                        <tr className="colHead">
                                                            <th>Skill</th>
                                                            <th style={{ width: 58, textAlign: "right" }}>Calls</th>
                                                            <th style={{ width: 58, textAlign: "right" }}>Orders</th>
                                                            <th style={{ width: 92, textAlign: "right" }}>Amount</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {group.items.map((item, ix) => {
                                                            const isGood = item.calls > 0 && item.perf >= 15;
                                                            const arrow = item.calls === 0 ? "\u2014" : isGood ? "\u2191" : "\u2193";

                                                            void arrow;
                                                            return (
                                                                <tr key={ix}>
                                                                    <td className="skillCell">
                                                                        {getSkillLabel(item)}
                                                                    </td>
                                                                    <td className="num-cell">{fmt(item.calls)}</td>
                                                                    <td className="num-cell">{fmt(item.orders)}</td>
                                                                    <td className="num-cell amount-cell">{formatOrderAmount(item.orderAmount)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                            ))}

                        {!isLoading && groupedData.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">No data for selected filters.</div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TodayLiveCallCenterReports);
